import type { Metadata } from "next"
import Link from "next/link"
import { ArrowRight, BookOpen, Calculator, CookingPot } from "lucide-react"
import { notFound } from "next/navigation"

import { Container } from "@/components/layout/container"
import { Breadcrumbs } from "@/components/layout/breadcrumbs"
import { RecipeCard } from "@/components/recipes/recipe-card"
import { SectionHeading } from "@/components/shared/section-heading"
import { KuhlykTip } from "@/components/kuhlyk/kyhlyk-tip"
import { getSeasonalHubBySlug } from "@/data/collections"
import { getAllRecipes } from "@/data/recipes"
import { getAllGuides } from "@/data/guides"
import { siteConfig } from "@/data/site"
import { absoluteUrl } from "@/lib/utils"

export const metadata: Metadata = {
  title: "Літній сезон заготовок",
  description:
    "Літній сезон заготовок на порталі Кухлик: огірки, ягоди, варення, компоти, кабачки, корисні гайди та інструменти для активного старту сезону.",
  alternates: {
    canonical: "/seasonal/summer",
  },
  openGraph: {
    title: "Літній сезон заготовок",
    description:
      "Літні рецепти, ягоди, огірки, варення, компоти та корисні інструменти для старту активного сезону.",
    url: absoluteUrl("/seasonal/summer", siteConfig.url),
    siteName: siteConfig.name,
    type: "website",
    images: [
      {
        url: absoluteUrl(siteConfig.ogImage, siteConfig.url),
        width: 1200,
        height: 630,
        alt: "Літній сезон заготовок",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    title: "Літній сезон заготовок",
    description:
      "Огірки, ягоди, компоти, варення, кабачки та активний старт сезону домашніх заготовок.",
    images: [absoluteUrl(siteConfig.ogImage, siteConfig.url)],
  },
}

export default function SummerSeasonalPage() {
  const hub = getSeasonalHubBySlug("summer")

  if (!hub) {
    notFound()
  }

  const allRecipes = getAllRecipes()
  const allGuides = getAllGuides()

  const featuredRecipes = allRecipes.filter((recipe) =>
    hub.featuredRecipeSlugs.includes(recipe.slug)
  )

  const featuredGuides = allGuides.filter((guide) =>
    hub.featuredGuideSlugs.includes(guide.slug)
  )

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: hub.title,
    description: hub.description,
    url: absoluteUrl("/seasonal/summer", siteConfig.url),
  }

  return (
    <>
      <script
        id="seasonal-summer-webpage-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <section className="border-b border-neutral-200 bg-neutral-50/70 py-5">
        <Container>
          <Breadcrumbs
            items={[
              { label: "Головна", href: "/" },
              { label: "Сезонні хаби", href: "/seasonal" },
              { label: "Літо" },
            ]}
          />
        </Container>
      </section>

      <section className="border-b border-neutral-200 bg-gradient-to-b from-yellow-50/60 to-white py-12 sm:py-16">
        <Container>
          <div className="max-w-4xl">
            <div className="eyebrow">Літній сезон</div>

            <div className="mt-4 text-5xl">{hub.icon}</div>

            <h1 className="mt-4 text-4xl font-semibold leading-tight text-neutral-950 sm:text-5xl">
              {hub.title}
            </h1>

            <p className="mt-5 text-base leading-8 text-neutral-700 sm:text-lg">
              {hub.description}
            </p>
          </div>
        </Container>
      </section>

      <section className="py-12 sm:py-14">
        <Container>
          <SectionHeading
            eyebrow="Сценарії сезону"
            title="Що найчастіше хвилює влітку"
            description="Літо — це старт активного сезону домашніх заготовок: огірки, ягоди, компоти, варення, перші кабачки й швидкі рішення для свіжого врожаю."
          />

          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {hub.scenarios.map((scenario) => (
              <div
                key={scenario}
                className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-sm"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
                  <CookingPot className="h-5 w-5" />
                </div>

                <h2 className="text-lg font-semibold text-neutral-950">
                  {scenario}
                </h2>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="border-y border-neutral-200 bg-neutral-50 py-12 sm:py-14">
        <Container>
          <SectionHeading
            eyebrow="Рецепти"
            title="Що готувати влітку"
            description="У центрі літнього сезону — огірки, ягоди, перші компоти, варення та зручні рецепти, з яких легко почати активну заготівлю."
          />

          <div className="mt-8 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {featuredRecipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </Container>
      </section>

      <section className="py-12 sm:py-14">
        <Container>
          <SectionHeading
            eyebrow="Гайди та поради"
            title="Що корисно прочитати влітку"
            description="На старті сезону особливо важливо правильно підготувати банки, продумати зберігання та спокійно організувати перші великі партії домашніх заготовок."
          />

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {featuredGuides.map((guide) => (
              <Link
                key={guide.slug}
                href={`/guides/${guide.slug}`}
                className="group block rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-sm transition-transform hover:-translate-y-0.5"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
                  <BookOpen className="h-5 w-5" />
                </div>

                <h2 className="text-xl font-semibold text-neutral-950 transition-colors group-hover:text-emerald-700">
                  {guide.title}
                </h2>

                <p className="mt-3 text-sm leading-7 text-neutral-600">
                  {guide.excerpt}
                </p>

                <div className="mt-5 inline-flex items-center text-sm font-semibold text-emerald-700">
                  Читати гайд
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <section className="border-y border-neutral-200 bg-neutral-50 py-12 sm:py-14">
        <Container>
          <SectionHeading
            eyebrow="Інструменти"
            title="Що допоможе в сезоні"
            description="Калькулятори особливо корисні влітку, коли потрібно швидко перерахувати маринад, масштабувати рецепт і не витрачати зайвий час на ручні підрахунки."
          />

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {hub.featuredCalculatorLinks.map((tool) => (
              <Link
                key={tool.href}
                href={tool.href}
                className="group block rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-sm transition-transform hover:-translate-y-0.5"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
                  <Calculator className="h-5 w-5" />
                </div>

                <h2 className="text-xl font-semibold text-neutral-950 transition-colors group-hover:text-emerald-700">
                  {tool.title}
                </h2>

                <p className="mt-3 text-sm leading-7 text-neutral-600">
                  {tool.description}
                </p>

                <div className="mt-5 inline-flex items-center text-sm font-semibold text-emerald-700">
                  Відкрити інструмент
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {hub.relatedLinks?.length ? (
        <section className="py-12 sm:py-14">
          <Container>
            <SectionHeading
              eyebrow="Корисно поруч"
              title="Що ще зручно відкрити влітку"
              description="Літній сезонний хаб добре працює разом із тематичними добірками та каталогом рецептів."
            />

            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {hub.relatedLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-sm transition-transform hover:-translate-y-0.5"
                >
                  <h3 className="text-lg font-semibold text-neutral-950">
                    {link.title}
                  </h3>
                  {link.description ? (
                    <p className="mt-3 text-sm leading-7 text-neutral-600">
                      {link.description}
                    </p>
                  ) : null}
                  <div className="mt-5 inline-flex items-center text-sm font-semibold text-emerald-700">
                    Перейти
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </Container>
        </section>
      ) : null}

      <section className="py-12 sm:py-14">
        <Container className="grid gap-4 lg:grid-cols-2">
          <KuhlykTip text="Літній сезон найкраще починати з простих і зрозумілих рецептів. Коли під рукою вже є перевірені базові заготовки, далі набагато легше розподіляти сили між огірками, ягодами, компотами й варенням." />
          <KuhlykTip
            variant="warm"
            title="Порада від Кухлика"
            text="У розпал літа особливо важливо не намагатися зробити все одразу. Краще обрати кілька пріоритетних напрямків сезону й рухатися партіями — так менше втоми й більше вдалих банок."
          />
        </Container>
      </section>
    </>
  )
}